import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

interface FormData {
  [key: string]: any;
}

// 1. UPDATED STATE INTERFACE
interface FormConfigState {
  data: {
    ROLE: string;
    USER_ID: string;
    cwid?: string;
  };
  user: any;
  accessRoles: any[]; // Full access array from API
  accessToken: string | null; // New field for the token
  loading: boolean;
  errorMessage: string | null;
  errorVisible: boolean;
  toastMessage: string | null;
  toastVisible: boolean;
  toastType: 'success' | 'error' | 'info';
}

// 2. UPDATED INITIAL STATE
const initialState: FormConfigState = {
  data: {
    ROLE: 'APPROVER',
    USER_ID: 'GPONM',
    cwid: 'GPONM'
  },
  user: {},
  accessRoles: [],
  accessToken: null, // New initial value
  loading: false,
  errorMessage: null,
  errorVisible: false,
  toastMessage: null,
  toastVisible: false,
  toastType: 'info',
};

// Async thunk remains the same
export const fetchFormQuestionsData = createAsyncThunk<FormData[]>(
  'data/fetchData',
  async (_, thunkAPI) => {
    // Simulating an API call
    const response = await new Promise<{ data: FormData[] }>((resolve) => {
      setTimeout(() => {
        resolve({ data: [] });
      }, 1000);
    });
    return response.data; // Return actual data
  }
);

// Create slice
const formConfig = createSlice({
  name: 'formConfig',
  initialState,
  reducers: {
    setCwid: (state, action: PayloadAction<string>) => {
      state.data.cwid = action.payload;
    },
    setCurrentUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
    },
    setAccessRoles: (state, action: PayloadAction<any[]>) => {
      state.accessRoles = action.payload;
    },
    switchRole: (state, action: PayloadAction<number>) => {
      const roleIndex = action.payload;
      const access = state.accessRoles;
      if (access && access[roleIndex]) {
        const selectedAccess = access[roleIndex];
        state.user = {
          ...state.user,
          role: selectedAccess.role,
          mappedLocations: selectedAccess.mappedLocations,
        };
      }
    },
    // New reducer for setting the access token
    setAccessToken: (state, action: PayloadAction<string | null>) => {
      state.accessToken = action.payload;
    },
    showError: (state, action: PayloadAction<{ message: string; visible: boolean }>) => {
      state.errorMessage = action.payload.message;
      state.errorVisible = action.payload.visible;
    },
    clearError: (state) => {
      state.errorMessage = null;
      state.errorVisible = false;
    },
    showToast: (state, action: PayloadAction<{ message: string; type?: 'success' | 'error' | 'info' }>) => {
      state.toastMessage = action.payload.message;
      state.toastType = action.payload.type || 'info';
      state.toastVisible = true;
    },
    hideToast: (state) => {
      state.toastMessage = null;
      state.toastVisible = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFormQuestionsData.pending, (state) => {
        state.loading = true;
        state.errorVisible = false;
        state.errorMessage = null;
      })
      .addCase(fetchFormQuestionsData.fulfilled, (state: any, action: PayloadAction<FormData[]>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchFormQuestionsData.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Data fetch failed unexpectedly.';
        state.errorVisible = true;
      });
  },
});

// EXPORT NEW ACTION
export const { setCwid, setCurrentUser, setAccessRoles, switchRole, setAccessToken, showError, clearError, showToast, hideToast } = formConfig.actions;
export default formConfig.reducer;
